import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/shared/utils/cn';
import { hslToRgb, rgbToHex, hexToHsl } from '../../utils';

interface ColorPaletteProps {
  /** Color actual en formato hexadecimal */
  color: string;
  /** Callback cuando cambia el color */
  onColorChange: (color: string) => void;
  /** Clase personalizada */
  className?: string;
}

/**
 * Componente molecule que muestra un selector de colores completo
 * con paleta de saturación/luminosidad y barra de matiz
 */
export const ColorPalette = ({ color, onColorChange, className }: ColorPaletteProps) => {
  const paletteRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingHue, setIsDraggingHue] = useState(false);

  // Convertir color a HSL
  const hsl = hexToHsl(color) || { h: 0, s: 100, l: 50 };
  const [hue, setHue] = useState(hsl.h);
  const [saturation, setSaturation] = useState(hsl.s);
  const [lightness, setLightness] = useState(hsl.l);

  // Sincronizar HSL cuando cambia el color externo
  useEffect(() => {
    const newHsl = hexToHsl(color);
    if (newHsl) {
      setHue(newHsl.h);
      setSaturation(newHsl.s);
      setLightness(newHsl.l);
    }
  }, [color]);

  // Actualizar color cuando cambian HSL
  const updateColor = useCallback(
    (h: number, s: number, l: number) => {
      const rgb = hslToRgb(h, s, l);
      const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
      onColorChange(hex);
    },
    [onColorChange]
  );

  // Manejar click/drag en la paleta de saturación/luminosidad
  const handlePaletteInteraction = useCallback(
    (clientX: number, clientY: number) => {
      if (!paletteRef.current) return;

      const rect = paletteRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));

      const newSaturation = Math.round(x * 100);
      const newLightness = Math.round(100 - y * 100);

      setSaturation(newSaturation);
      setLightness(newLightness);
      updateColor(hue, newSaturation, newLightness);
    },
    [hue, updateColor]
  );

  // Manejar click/drag en la barra de matiz
  const handleHueInteraction = useCallback(
    (clientX: number) => {
      if (!hueRef.current) return;

      const rect = hueRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const newHue = Math.round(x * 360);

      setHue(newHue);
      updateColor(newHue, saturation, lightness);
    },
    [saturation, lightness, updateColor]
  );

  // Event handlers para mouse
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    handlePaletteInteraction(e.clientX, e.clientY);
  };

  const handleHueMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingHue(true);
    handleHueInteraction(e.clientX);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        handlePaletteInteraction(e.clientX, e.clientY);
      } else if (isDraggingHue) {
        handleHueInteraction(e.clientX);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsDraggingHue(false);
    };

    if (isDragging || isDraggingHue) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isDraggingHue, handlePaletteInteraction, handleHueInteraction]);

  // Generar gradiente para la paleta (saturación y luminosidad)
  const paletteStyle = {
    background: `linear-gradient(to top, #000 0%, transparent 50%, #fff 100%), linear-gradient(to right, hsl(${hue}, 100%, 50%) 0%, transparent 100%)`,
  };

  // Generar gradiente para la barra de matiz
  const hueGradient = `linear-gradient(to right, 
    hsl(0, 100%, 50%) 0%,
    hsl(60, 100%, 50%) 16.66%,
    hsl(120, 100%, 50%) 33.33%,
    hsl(180, 100%, 50%) 50%,
    hsl(240, 100%, 50%) 66.66%,
    hsl(300, 100%, 50%) 83.33%,
    hsl(360, 100%, 50%) 100%
  )`;

  // Posición del selector en la paleta
  const selectorX = (saturation / 100) * 100;
  const selectorY = 100 - (lightness / 100) * 100;

  return (
    <div className={cn('ui-flex ui-flex-col ui-gap-3', className)}>
      {/* Paleta de saturación/luminosidad */}
      <div className="ui-relative">
        <div
          ref={paletteRef}
          className="ui-relative ui-h-[170px] ui-w-full ui-cursor-crosshair ui-overflow-hidden ui-rounded-surface-card"
          style={paletteStyle}
          onMouseDown={handleMouseDown}
        >
          {/* Selector circular */}
          <div
            className="ui-pointer-events-none ui-absolute ui-h-4 ui-w-4 ui--translate-x-1/2 ui--translate-y-1/2 ui-transform ui-rounded-full ui-border-2 ui-border-white ui-shadow-02"
            style={{
              left: `${selectorX}%`,
              top: `${selectorY}%`,
            }}
          />
        </div>
      </div>

      {/* Barra de matiz (hue) */}
      <div className="ui-relative">
        <div
          ref={hueRef}
          className="ui-relative ui-h-16 ui-w-full ui-cursor-pointer ui-overflow-hidden ui-rounded-control-pill"
          style={{ background: hueGradient }}
          onMouseDown={handleHueMouseDown}
        >
          {/* Selector de matiz */}
          <div
            className="ui-pointer-events-none ui-absolute ui-bottom-0 ui-top-0 ui-w-1 ui--translate-x-1/2 ui-transform ui-bg-white ui-shadow-02"
            style={{
              left: `${(hue / 360) * 100}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};
